import { apiClient, ApiClient } from "@/lib/api/api-client";
import { SortType } from "@/modules/shared/types/enum";
import {
  BulkImportCVResponse,
  DraftJob,
  FilterJobStatus,
  GetBulkImportBatchStatusResponse,
  Job,
  JobCreate,
  JobScoringConfigResponse,
  JobStatus,
  SortByJobField,
  UpdateJobScoringConfigPayload,
} from "@/modules/types/job";
import { PostType } from "@/modules/types/metadata/metadata";
import { BaseResponse } from "@/modules/types/response";

export interface GetListDraftJobsResp extends BaseResponse {
  drafts?: DraftJob[];
  total?: number;
  nextId?: string;
}

export interface JobCreateResp extends BaseResponse {
  job: Job;
}

export interface GetListJobResp extends BaseResponse {
  jobs: Job[];
  total?: number;
  nextId?: string;
}

export interface GetJobResp extends BaseResponse {
  job?: Job;
}

export class jobService {
  private BASE_PATH =
    process.env.API_JOB_ENDPOINT || "http://localhost:8003/job";
  private apiClient: ApiClient = apiClient;
  private apiVersion = "v1.0.0";
  constructor(apiVersion?: string) {
    this.apiVersion = apiVersion || this.apiVersion;
  }

  async getDrafts(
    businessId: string,
    keyword?: string,
    limit?: number,
    nextId?: string,
  ): Promise<GetListDraftJobsResp> {
    const queryParam = new URLSearchParams();
    queryParam.append("businessId", businessId);
    if (keyword) queryParam.append("keyword", keyword);
    if (limit) queryParam.append("limit", limit.toString());
    if (nextId) queryParam.append("nextId", nextId);
    return await this.apiClient.fetch<GetListDraftJobsResp>(
      `${this.BASE_PATH}/${this.apiVersion}/draft/list?${queryParam.toString()}`,
    );
  }

  async createJob(job: JobCreate): Promise<JobCreateResp> {
    return await this.apiClient.fetch<JobCreateResp>(
      `${this.BASE_PATH}/${this.apiVersion}/create`,
      {
        method: "POST",
        body: JSON.stringify(job),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
  }

  async renewJob(businessId: string, jobId: string, postType: PostType) {
    return await this.apiClient.fetch<boolean>(
      `${this.BASE_PATH}/${this.apiVersion}/renew/${jobId}`,
      {
        method: "PUT",
        body: JSON.stringify({ postType, businessId }),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
  }

  async createDraftJob(job: JobCreate): Promise<JobCreateResp> {
    return await this.apiClient.fetch<JobCreateResp>(
      `${this.BASE_PATH}/${this.apiVersion}/draft`,
      {
        method: "POST",
        body: JSON.stringify(job),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
  }

  async getsByBusiness(
    businessId: string,
    keyword?: string,
    page?: number,
    limit?: number,
    status?: JobStatus,
    filterStatus?: FilterJobStatus,
    sortBy?: SortByJobField,
    sortOrder?: SortType,
  ): Promise<GetListJobResp> {
    const queryParam = new URLSearchParams();
    if (keyword) queryParam.append("jobTitle", keyword);
    if (page) queryParam.append("page", page.toString());
    if (limit) queryParam.append("limit", limit.toString());
    if (status) queryParam.append("status", status);
    if (filterStatus && filterStatus == FilterJobStatus.active)
      queryParam.append("status", filterStatus);
    if (filterStatus && filterStatus == FilterJobStatus.recruited)
      queryParam.append("recruited", "true");
    if (filterStatus && filterStatus == FilterJobStatus.expired)
      queryParam.append("expired", "true");
    if (sortBy) queryParam.append("sortBy", sortBy);
    if (sortOrder) queryParam.append("sortType", sortOrder);
    return this.apiClient.fetch<GetListJobResp>(
      `${this.BASE_PATH}/${this.apiVersion}/owner-jobs/${businessId}?${queryParam.toString()}`,
    );
  }

  async getOwnJobById(businessId: string, jobId: string): Promise<GetJobResp> {
    return await this.apiClient.fetch<GetJobResp>(
      `${this.BASE_PATH}/${this.apiVersion}/detail/business/${businessId}/id/${jobId}`,
    );
  }

  async extractJobRubric(jobId: string): Promise<JobScoringConfigResponse> {
    return await this.apiClient.fetch<JobScoringConfigResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/jobs/${jobId}/extract-rubric`,
      {
        method: "POST",
      },
    );
  }

  async getJobScoringConfig(jobId: string): Promise<JobScoringConfigResponse> {
    return await this.apiClient.fetch<JobScoringConfigResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/jobs/${jobId}/scoring-config`,
      {
        method: "GET",
      },
    );
  }

  async updateJobScoringConfig(
    jobId: string,
    data: UpdateJobScoringConfigPayload,
  ): Promise<JobScoringConfigResponse> {
    return await this.apiClient.fetch<JobScoringConfigResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/jobs/${jobId}/scoring-config`,
      {
        method: "PUT",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
  }

  async bulkImportCvs(
    jobId: string,
    files: File[],
  ): Promise<BulkImportCVResponse> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    return await this.apiClient.fetch<BulkImportCVResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/jobs/${jobId}/bulk-import-cv`,
      {
        method: "POST",
        body: formData,
      },
    );
  }

  async getBulkImportBatchStatus(
    jobId: string,
    batchId: string,
  ): Promise<GetBulkImportBatchStatusResponse> {
    return await this.apiClient.fetch<GetBulkImportBatchStatusResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/jobs/${jobId}/bulk-import-cv/${batchId}`,
      {
        method: "GET",
      },
    );
  }
}
